import React, { useState, useCallback, useRef } from 'react';
import { Input, Modal, Table, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { lookupApi, LookupResult } from '../services/lookupApi';

interface LookupFieldProps {
  /** Backend lookup type — maps to /api/lookup/{lookupType} */
  lookupType: string;
  value: string;
  onChange: (code: string, name?: string) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  placeholder?: string;
  /** Modal title, defaults to "搜尋" */
  title?: string;
  /** Max length for the text input */
  maxLength?: number;
  /** Called when user confirms a code (modal select or blur) — for LookupInput compatibility */
  onCodeConfirm?: (code: string) => void;
  contractFieldId?: string;
}

const columns = [
  { title: '代碼', dataIndex: 'code', key: 'code', width: 140 },
  { title: '名稱', dataIndex: 'name', key: 'name' },
];

const LookupField: React.FC<LookupFieldProps> = ({
  lookupType, value, onChange, disabled, style, placeholder, title, maxLength, onCodeConfirm,
  contractFieldId,
}) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<LookupResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const searchRef = useRef<any>(null);

  const doSearch = useCallback(async (kw?: string) => {
    setLoading(true);
    try {
      const res = await lookupApi.search(lookupType, kw || undefined, 1, 50);
      setData(res.data || []);
    } catch {
      setData([]);
    }
    setLoading(false);
  }, [lookupType]);

  const handleOpen = useCallback(() => {
    setKeyword('');
    setOpen(true);
    doSearch();
    // Auto-focus search input after modal opens
    setTimeout(() => searchRef.current?.focus(), 100);
  }, [doSearch]);

  const handleSelect = useCallback((record: LookupResult) => {
    onChange(record.code, record.name);
    onCodeConfirm?.(record.code);
    setOpen(false);
  }, [onChange, onCodeConfirm]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setKeyword(v);
    doSearch(v);
  }, [doSearch]);

  return (
    <>
      <Space.Compact style={style}>
        <Input
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={() => onCodeConfirm?.(value)}
          disabled={disabled}
          placeholder={placeholder}
          maxLength={maxLength}
          style={{ flex: 1 }}
          data-contract-field={contractFieldId}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={handleOpen}
          style={{
            border: '1px solid #d9d9d9', borderLeft: 0, background: '#fafafa',
            cursor: disabled ? 'not-allowed' : 'pointer', padding: '4px 8px',
            display: 'flex', alignItems: 'center',
          }}
        >
          <SearchOutlined />
        </button>
      </Space.Compact>

      <Modal
        title={title || '搜尋'}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={480}
        destroyOnClose
      >
        <Input
          ref={searchRef}
          placeholder="輸入代碼或名稱搜尋..."
          value={keyword}
          onChange={handleSearchChange}
          allowClear
          style={{ marginBottom: 12 }}
          prefix={<SearchOutlined />}
        />
        <Table
          dataSource={data}
          columns={columns}
          rowKey="code"
          size="small"
          loading={loading}
          pagination={false}
          scroll={{ y: 320 }}
          onRow={(record) => ({
            onClick: () => handleSelect(record),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: loading ? '搜尋中...' : '無資料' }}
        />
      </Modal>
    </>
  );
};

export default React.memo(LookupField);
